import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

/**
 * Custom hook to subscribe to a Server-Sent Events (SSE) stream for a specific task.
 * Aligns with the project's SSE middleware pattern.
 *
 * @param {string} taskId - The UUID of the task to monitor.
 * @param {object} options - Configuration options.
 * @param {function} [options.onComplete] - Callback when task status is COMPLETED or FAILED.
 * @returns {object} { taskState, isConnected: !!taskState, error: null }
 */
const useTaskStream = (taskId, options = {}) => {
  const { onComplete } = options;
  const dispatch = useDispatch();
  const onCompleteRef = useRef(onComplete);
  const completedForTaskRef = useRef("");

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Select task state from Redux store
  const taskState = useSelector(state => state.notification?.tasks?.[taskId]);

  useEffect(() => {
    if (!taskId) return undefined;
    completedForTaskRef.current = "";

    const url = `sse_ntf/tasks/${taskId}/stream/`;
    const key = `task-${taskId}`;

    // Start SSE via middleware
    dispatch({
      type: "fileSSE/start",
      payload: {
        category: "task",
        events: ["sync", "update", "complete", "error"],
        key,
        url,
      },
    });

    return () => {
      // Stop SSE via middleware
      dispatch({
        type: "fileSSE/stop",
        payload: { key },
      });
    };
  }, [taskId, dispatch]);

  useEffect(() => {
    if (!taskId) return;
    const terminal = ["COMPLETED", "DISMISSED", "FAILED", "FAILURE", "REVOKED", "SUCCESS"].includes(
      String(taskState?.status || "").toUpperCase(),
    );
    if (!terminal) return;
    // Fire once per taskId — unstable parent onComplete used to refetch in a loop.
    if (completedForTaskRef.current === taskId) return;
    completedForTaskRef.current = taskId;
    dispatch({
      type: "fileSSE/stop",
      payload: { key: `task-${taskId}` },
    });
    if (typeof onCompleteRef.current === "function") {
      onCompleteRef.current(taskState);
    }
  }, [dispatch, taskId, taskState]);

  return { taskState, isConnected: !!taskState, error: null };
};

export default useTaskStream;
